/** The memory frame's details pocket, one level at a time. */
import { forwardRef, useEffect, useRef } from "react";
import type { ThingKind } from "@/api/memory-focus";
import { PocketHeader, PocketPanel } from "@/components/ui";
import { useT, type Translator } from "@/i18n";
import { framesMemoryMessages } from "@/i18n/messages/frames-memory";
import type en from "@/i18n/messages/frames-memory/en";
import { cn } from "@/lib/cn";
import type { PocketEntry } from "@/stores/memory-pocket-stack";
import { useMemoryStore, type MemoryState } from "@/stores/memory-store";
import { bundleTitle, findFact } from "./memory-pocket-helpers";
import { PocketBundleLevel } from "./pocket-bundle-level";
import { PocketFactLevel } from "./pocket-fact-level";
import { PocketHistoryLevel } from "./pocket-history-level";
import { PocketSearchLevel } from "./pocket-search-level";
import { PocketSourcesLevel } from "./pocket-sources-level";
import { PocketSuggestionLevel } from "./pocket-suggestion-level";
import { PocketThingLevel } from "./pocket-thing-level";

const TITLE_KEY = {
  search: "memory",
  suggestion: "review",
  sources: "sources",
  history: "history",
} as const satisfies Record<
  Exclude<PocketEntry["level"], "thing" | "fact" | "bundle">,
  keyof typeof en
>;

function levelTitle(
  state: MemoryState,
  entry: PocketEntry,
  t: Translator<typeof en>,
): string {
  if (entry.level === "thing") return state.thingNames[entry.id] ?? t("memory");
  if (entry.level === "fact")
    return findFact(state, entry.id)?.claim ?? t("fact");
  if (entry.level === "bundle")
    return state.focusView
      ? bundleTitle(state.focusView, entry.id as ThingKind)
      : t("list");
  return t(TITLE_KEY[entry.level]);
}

function PocketLevelView({
  entry,
  youName,
}: {
  entry: PocketEntry;
  youName: string;
}) {
  switch (entry.level) {
    case "search":
      return <PocketSearchLevel youName={youName} />;
    case "thing":
      return <PocketThingLevel id={entry.id} youName={youName} />;
    case "fact":
      return <PocketFactLevel factId={entry.id} />;
    case "suggestion":
      return <PocketSuggestionLevel id={entry.id} />;
    case "bundle":
      return <PocketBundleLevel kind={entry.id as ThingKind} />;
    case "sources":
      return <PocketSourcesLevel itemId={entry.id} />;
    case "history":
      return <PocketHistoryLevel itemId={entry.id} />;
  }
}

export const MemoryPocket = forwardRef<
  HTMLDivElement,
  {
    youName: string;
    className?: string;
  }
>(function MemoryPocket({ youName, className }, ref) {
  const t = useT(framesMemoryMessages);
  const stack = useMemoryStore((state) => state.pocketStack);
  const parentTitle = useMemoryStore((state) =>
    state.pocketStack.length > 1
      ? levelTitle(state, state.pocketStack[state.pocketStack.length - 2]!, t)
      : null,
  );
  const depth = useRef(stack.length);
  const motion =
    stack.length > depth.current
      ? "in"
      : stack.length < depth.current
        ? "out"
        : null;
  useEffect(() => {
    depth.current = stack.length;
  }, [stack.length]);
  const top = stack[stack.length - 1];
  if (!top) return null;

  return (
    <PocketPanel
      ref={ref}
      nativeGroup="memory-pocket"
      nativeSurface={false}
      testId="memory-pocket"
      role="region"
      aria-label={t("memoryDetails")}
      className={cn(
        "nodrag nopan nowheel pointer-events-auto w-[280px] select-text",
        className,
      )}
    >
      <PocketHeader
        title={parentTitle ?? t("memory")}
        onBack={
          parentTitle === null
            ? undefined
            : () => useMemoryStore.getState().popPocket()
        }
        onClose={() => useMemoryStore.getState().closePocket()}
        testIdPrefix="memory-pocket"
      />
      <div
        key={`${stack.length}:${top.level}:${top.id}`}
        data-testid="memory-pocket-body"
        className={cn(
          "-mx-1 min-h-0 flex-1 overflow-y-auto overscroll-contain px-1 pb-1",
          motion === "in" && "motion-safe:animate-pocket-in",
          motion === "out" && "motion-safe:animate-pocket-out",
        )}
      >
        <PocketLevelView entry={top} youName={youName} />
      </div>
    </PocketPanel>
  );
});
